import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Loader2, Sparkles, ShieldCheck, IndianRupee } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  const _auth = () => {
    const t = localStorage.getItem('token');
    const base = { 'Content-Type': 'application/json' };
    return t ? { ...base, Authorization: `Bearer ${t}` } : base;
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/subscription/status`, { credentials: 'include', headers: _auth() });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSubscribe = async (plan) => {
    setCheckingOut(plan);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Could not load Razorpay checkout. Please retry.'); setCheckingOut(null); return; }

      const r = await fetch(`${API}/api/subscription/create-order`, {
        method: 'POST', credentials: 'include', headers: _auth(), body: JSON.stringify({ plan }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not start checkout. Try again.');
        setCheckingOut(null); return;
      }
      const { key_id, order_id, amount_paise, amount_inr } = await r.json();

      const rzp = new window.Razorpay({
        key: key_id,
        order_id,
        amount: amount_paise,
        currency: 'INR',
        name: 'CognitiaIndia',
        description: plan === 'monthly'
          ? '₹399 / month — 30 days access'
          : plan === 'family'
            ? '₹599 / month — Family Plan (up to 3 siblings)'
            : '₹4453 / year — 365 days access (7% off)',
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.parent_mobile || '' },
        theme: { color: '#6366f1' },
        handler: async (response) => {
          const verify = await fetch(`${API}/api/subscription/verify-payment`, {
            method: 'POST', credentials: 'include', headers: _auth(),
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          if (verify.ok) {
            const data = await verify.json();
            toast.success(`Subscription activated! Valid till ${new Date(data.expires_at).toLocaleDateString()}`);
            fetchStatus();
          } else {
            const err = await verify.json().catch(() => ({}));
            toast.error(err.detail || 'Payment verification failed.');
          }
        },
        modal: { ondismiss: () => setCheckingOut(null) },
      });
      rzp.on('payment.failed', (e) => toast.error(e?.error?.description || 'Payment failed. No amount charged.'));
      rzp.open();
    } catch (e) {
      toast.error(e?.message || 'Something went wrong.');
    } finally {
      setCheckingOut(null);
    }
  };

  const plans = [
    {
      id: 'monthly', price: 399, per: 'month', badge: 'Popular',
      accent: 'from-indigo-500 to-fuchsia-500',
      bullets: [
        'Full access to Learning Hub, CodeLab and Practice Engine',
        '10 AI-assisted actions every day (concept narration, exam builder)',
        'Weekly progress digest + streak nudges',
        'Cancel anytime — cancel next renewal',
      ],
    },
    {
      id: 'yearly', price: 4453, per: 'year', badge: 'Save 7%',
      accent: 'from-emerald-500 to-teal-500',
      bullets: [
        'Everything in Monthly, billed annually',
        'Saves ₹335 vs paying monthly for 12 months',
        'Priority support during exam season',
        'Same 10 AI-assisted actions per day',
      ],
    },
    {
      id: 'family', price: 599, per: 'month', badge: 'Best for siblings',
      accent: 'from-amber-500 to-orange-500',
      bullets: [
        'Up to 3 siblings on one plan — you pay just ₹200/sibling',
        'Shared parent dashboard tracks every child in one place',
        'Each child keeps their own AI quota (10/day)',
        'Invite siblings by email, add or swap anytime',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500/5 via-background to-emerald-500/5 py-10 px-4" data-testid="subscription-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black">Choose your plan</h1>
          <p className="text-sm text-muted-foreground mt-2">All prices include 18% GST · UPI, cards, netbanking supported via Razorpay</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : (
          <>
            {status?.subscription_active && (
              <Card className="mb-6 border-emerald-500/30 bg-emerald-500/5" data-testid="active-sub-banner">
                <CardContent className="p-4 flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">You are all set.</p>
                    <p className="text-xs text-muted-foreground">
                      {status.trial_active
                        ? `Free trial active until ${new Date(status.trial_ends_at).toLocaleDateString()}.`
                        : `${status.subscription_plan || 'Plan'} active until ${new Date(status.subscription_expires_at).toLocaleDateString()}.`}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="active-goto-dashboard">Go to Dashboard</Button>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {plans.map((p) => (
                <Card key={p.id} className="relative overflow-hidden" data-testid={`plan-card-${p.id}`}>
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${p.accent}`} />
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg capitalize">{p.id}</CardTitle>
                      <Badge className={`bg-gradient-to-r ${p.accent} text-white border-0`}>{p.badge}</Badge>
                    </div>
                    <div className="flex items-baseline gap-1 mt-2">
                      <IndianRupee className="w-5 h-5" />
                      <span className="text-4xl font-black">{p.price}</span>
                      <span className="text-sm text-muted-foreground">/ {p.per}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Inclusive of 18% GST</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full"
                      disabled={checkingOut === p.id}
                      onClick={() => handleSubscribe(p.id)}
                      data-testid={`subscribe-btn-${p.id}`}
                    >
                      {checkingOut === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Subscribe {p.id}</>}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <p className="text-center text-[11px] text-muted-foreground mt-6">Secure payments powered by Razorpay · your card details never touch our servers.</p>
          </>
        )}
      </div>
    </div>
  );
}
